import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CatalogsService } from '../../../catalogs/services/catalogs.services';
import { MatDialog } from '@angular/material/dialog';
import { ICatalog, ICatalogFilter, ICatalogFilterRequest } from '../../../catalogs/models/catalogs.models';
import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged, Observable, Subject, Subscription, take, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { environment } from 'src/environments/environment';
import { UiModule } from 'src/app/ui/ui.module';
import { CatalogsFilterComponent } from '../../../catalogs/components/catalogs-filter/catalogs-filter.component';
import { encodeFilterObject, refillFilter } from 'src/app/shared/utils/helpers';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { GetProductsCatalogCompanyById, GetProductsCatalogCompanyByIddSuccess, SetCatalogsFilter } from '../../../catalogs/store/catalogs.actions';
import { NavigationService } from 'src/app/shared/router.service';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import { IPageCount } from 'src/app/shared/models/page.model';
import { Actions, ofType } from '@ngrx/effects';

@Component({
  selector: 'app-explore-catalog',
  standalone: true,
  imports: [CommonModule, SharedModule, UiModule],
  templateUrl: './explore-catalog.component.html',
  styleUrls: ['./explore-catalog.component.scss']
})
export class ExploreCatalogComponent implements OnInit, OnDestroy {
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1
  }
  @Input() location_id!: number;

  @Output() selectedChange = new EventEmitter<ICatalog[]>();
  @Output() changeComponent = new EventEmitter<ICatalog>();

  catalogSelected: ICatalog | null = null;
  API_URL = environment.BASE_URL_API;
  DATE_FORMAT = 'shortDate';

  catalogs: ICatalog[] = [];
  selectedCatalogs: number[] = [];
  allCatalogs: ICatalog[] = [];

  isLoading = true;
  isLoadingMore = false;
  canLoadMore = true;
  pageParams: any = { number: 1, size: DEFAULT_PAGE_SIZE };

  searchControl: FormControl = new FormControl(undefined);
  searchControlFocused = false;
  isCardView = true;
  showScrollTop = false;
  failedImages = new Set<number>();
  filter: ICatalogFilterRequest | undefined = undefined;
  unparsedFilter: ICatalogFilter | null = null;
  module: string = 'catalog';

  private subs = new Subscription();
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private catalogsService: CatalogsService,
    private router: Router,
    private elementRef: ElementRef,
    private navigationService: NavigationService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private actions$: Actions,
    private featureFlags: FeatureFlagService
  ) { }

  ngOnInit(): void {
    this.loadCatalogs();

    const s = this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.pageParams.number = 1;
        this.canLoadMore = true;
        this.applySearchFilter();
      });

    this.actions$
      .pipe(ofType(GetProductsCatalogCompanyByIddSuccess), takeUntil(this.destroy$))
      .subscribe(() => (
        this.changeComponent.emit(this.catalogSelected!)
      ));

    this.subs.add(s);
  }

  loadCatalogs(loadMore = false): void {
    const locId = this.location_id || 0;

    if (!loadMore) {
      this.isLoading = true;
      this.pageParams.number = 1;
    } else {
      this.isLoadingMore = true;
    }

    this.catalogsService
      .listAllCompanyCatalogsById(locId)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const data = Array.isArray((res as any).data)
            ? (res as any).data
            : ((res as any).data ? [(res as any).data] : []);

          const approved = data.filter((c: ICatalog) => c.status === 'Approved');
          this.allCatalogs = approved;
          const start = (this.pageParams.number - 1) * this.pageParams.size;
          const end = start + this.pageParams.size;
          const chunk = approved.slice(start, end);

          if (!loadMore) {
            this.catalogs = chunk;
            this.selectedCatalogs = [];
            this.emitSelected();
          } else {
            this.catalogs = [...this.catalogs, ...chunk];
          }

          this.canLoadMore = end < approved.length;

          this.isLoading = false;
          this.isLoadingMore = false;
        },
        error: () => {
          this.isLoading = false;
          this.isLoadingMore = false;
          this.canLoadMore = false;
        }
      });
  }

  applySearchFilter(): void {
    const search = (this.searchControl.value || '').trim().toLowerCase();

    if (!search) {
      this.catalogs = [...this.allCatalogs];
      this.canLoadMore = this.allCatalogs.length >= (this.pageParams.size || DEFAULT_PAGE_SIZE);
    } else {
      this.catalogs = this.allCatalogs.filter(c =>
        c.name.toLowerCase().includes(search) ||
        (c.description && c.description.toLowerCase().includes(search))
      );
      this.canLoadMore = false;
    }
  }

  loadMore(): void {
    const search = (this.searchControl.value || '').trim();
    if (search) return;

    if (!this.canLoadMore || this.isLoadingMore) return;
    this.pageParams.number = (this.pageParams.number || 1) + 1;
    this.loadCatalogs(true);
  }

  get isAllCatalogsSelected(): boolean {
    const allIds = this.catalogs.map((c) => c.id);
    return allIds.length > 0 && allIds.every((id) => this.selectedCatalogs.includes(id));
  }

  get isPartialSelectedAll(): boolean {
    const allIds = this.catalogs.map((c) => c.id);
    const selectedCount = allIds.filter((id) => this.selectedCatalogs.includes(id)).length;
    return selectedCount > 0 && selectedCount < allIds.length;
  }

  toggleAllCatalogs(checked: boolean): void {
    const allIds = this.catalogs.map((c) => c.id);
    this.selectedCatalogs = checked ? [...allIds] : [];
    this.emitSelected();
  }

  toggleCatalog(catalogId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedCatalogs.includes(catalogId)) this.selectedCatalogs.push(catalogId);
    } else {
      this.selectedCatalogs = this.selectedCatalogs.filter((id) => id !== catalogId);
    }
    this.emitSelected();
  }

  private emitSelected(): void {
    const selected = this.catalogs.filter((c) => this.selectedCatalogs.includes(c.id));
    this.selectedChange.emit(selected);
  }

  gotoCatalogProducts(catalog: ICatalog): void {
    this.router.navigate(['catalogs', catalog.id, 'products']);
  }

  onImageError(catalogId: number): void {
    this.failedImages.add(catalogId);
  }

  scrollToTop(): void {
    const container = this.elementRef.nativeElement.querySelector('.catalogs-viewport');
    if (container && container.scrollTo) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  get isScrollable(): boolean {
    return this.catalogs.length > 10;
  }

  get isSkeletonLoaderActive(): boolean {
    return this.isLoading || this.isLoadingMore;
  }

  filters: any = {}
  search(event: any) {
    const key = event.target.value;

    if (this.filters.keyword != key) {
      this.filters.keyword = key
      this.store.dispatch(SetCatalogsFilter(this.filters))
      let queryParams: any = {
        page: 1,
        count: this.page.size,
      }
      if (key != '') {
        queryParams.keyword = key;
      }
      else {
        queryParams.keyword = null;
      }
      this.router.navigate([], {
        relativeTo: this.navigationService.getCurrentRoute(),
        queryParams: { ...queryParams },
        queryParamsHandling: 'merge'
      })
    }
  }
  setFilter() {
    const dialogRef = this.dialog.open(CatalogsFilterComponent, {
      minWidth: '650px',
      width: '90vw',
      height: '80vh',
    });
    dialogRef.afterClosed().subscribe((filterResult: any) => {
      if (filterResult) {
        const unparsedFilter = filterResult;
        const encoded = encodeFilterObject(unparsedFilter);
        this.router.navigate([], {
          queryParams: {
            page: 1,
            filter: encoded
          },
          queryParamsHandling: 'merge',
        });
      }
    });
  }
  hasModuleFilters(): boolean {
    if (this.filter)
      return this.filter.filters && this.filter?.filters?.length > 0;
    return false;
  }

  clearFilter() {
    this.filters.advanced = refillFilter(this.module, null);
    this.store.dispatch(SetCatalogsFilter(this.filters))
  }
  displayText(value: string | undefined | null, size = 20): string {
    if (!value) return '';
    return value.length > size ? value.slice(0, size) + '…' : value;
  }

  getCatalogTags(catalog: ICatalog): { label: string; tag: string }[] {
    const tags: { label: string; tag: string }[] = [];

    const isNew = (d?: any) => {
      if (!d) return false;
      const date = new Date(d);
      const diff = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
      return diff <= 7;
    };

    const isExpiring = (end?: any) => {
      if (!end) return false;
      const endDate = new Date(end);
      const diff = (endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return diff >= 0 && diff <= 7;
    };

    if (catalog.create_date) {
      if (catalog.end_date) {
        if (isExpiring(catalog.end_date)) {
          tags.push({ label: this.translate.instant('explore_catalogs.expiring'), tag: 'warning' });
        } else if (isNew(catalog.create_date)) {
          tags.push({ label: this.translate.instant('explore_catalogs.new'), tag: 'success' });
        }
      } else if (isNew(catalog.create_date)) {
        tags.push({ label: this.translate.instant('explore_catalogs.new'), tag: 'success' });
      }
    }

    if (this.featureFlags.loyaltyEnabled && (catalog as any).new_deal) {
      tags.push({ label: this.translate.instant('explore_catalogs.deals'), tag: 'info' });
    }

    const nb = catalog.nbr_brands || 0;
    if (nb === 1) {
      tags.push({ label: this.translate.instant('explore_catalogs.monobrand'), tag: 'secondary' });
    } else if (nb > 1) {
      tags.push({ label: this.translate.instant('explore_catalogs.multibrand'), tag: 'secondary' });
    }

    return tags;
  }

  goTo(catalog: ICatalog): void {
    this.catalogSelected = catalog;
    // TODO: implement pagination with lazy loading and remove the pagination details
    this.store.dispatch(GetProductsCatalogCompanyById({ locationId: this.location_id, catalogId: catalog.id, page: 1, limit: 500 }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
